import { useState, useEffect } from 'react'
import cnames from 'classnames'
import useBlackoutMutations from 'Shared/CalendarBlock/hooks/useBlackoutMutations'
import { Button, Alert } from 'reactstrap'
import ViewModal from 'Shared/ViewModal/ViewModal'
import CalendarBlockForm from './CalendarBlockForm'
import DeleteBlockedModal from './DeleteBlockedModal'

const getDate = date => {
  if (!date) return
  const [month, day, year] = date.split('/')
  const formatDate = `${year}-${month}-${day}`

  return formatDate.toString()
}

const CalendarBlock = ({ isEngineAdmin }) => {
  // State and modal logic
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [modal, setModal] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [success, setSuccess] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selected, setSelected] = useState('')
  const [fromEdit, setFromEdit] = useState('')
  const [toEdit, setToEdit] = useState('')
  const [siteEdit, setSiteEdit] = useState('')
  const [blackoutId, setBlackoutId] = useState('')
  const [reasonEdit, setReasonEdit] = useState('')
  const data = window.adminCalendarDateSelectionModal

  const fromElement = document.getElementById('from')
  const toElement = document.getElementById('to')
  const from = fromElement?.value ? getDate(fromElement.value) : null
  const to = toElement?.value ? getDate(toElement.value) : null
  const calendarModalClasses = cnames('calendar-block-modal', {
    'calendar-modal-active': dropdownOpen,
    'calendar-modal': !dropdownOpen,
  })

  // Custom hook for mutations
  const { handleBlockSite, handleUpdateSite, loading, error, updating, updateError, resetErrors } =
          useBlackoutMutations({ from, to, blackoutId, selected, setSuccess, data })

  const toggle = () => {
    setModal(m => !m)
    setEditMode(false)
  }

  const toggleEdit = e => {
    setFromEdit(e.currentTarget.getAttribute('data-from'))
    setToEdit(e.currentTarget.getAttribute('data-to'))
    setSiteEdit(e.currentTarget.getAttribute('data-site'))
    setBlackoutId(e.currentTarget.getAttribute('data-id'))
    setReasonEdit(e.currentTarget.getAttribute('data-reason'))
    setModal(m => !m)
    setEditMode(e => !e)
  }

  useEffect(() => {
    document.getElementById('open-modal-btn')?.addEventListener('click', toggle)
    return () => {
      document.getElementById('open-modal-btn')?.removeEventListener('click', toggle)
    }
  }, [toggle])

  useEffect(() => {
    const container = document.querySelector('.clusterize-scroll')

    const handleClick = e => {
      const target = e.target.closest('.edit-blocked-date')
      if (target) {
        // Create a synthetic event with the correct currentTarget
        const syntheticEvent = { currentTarget: target }
        toggleEdit(syntheticEvent)
      }
    }

    if (container) container.addEventListener('click', handleClick)

    return () => {
      if (container) container.removeEventListener('click', handleClick)
    }
  }, [toggleEdit])

  useEffect(() => {
    const modalDialog = document.querySelector('.view-modal')
    if (!modalDialog) return

    if (dropdownOpen) {
      // Use requestAnimationFrame to measure after DOM updates
      requestAnimationFrame(() => {
        const modalElement = document.querySelector('.view-modal .modal-content')
        if (modalElement) {
          const contentHeight = modalElement.offsetHeight
          modalDialog.style.setProperty('--card-height', `${contentHeight}px`)
        }
      })
      return
    }

    // Reset the height when dropdown closes
    modalDialog.style.removeProperty('--card-height')
  }, [dropdownOpen])

  const onClosed = () => {
    setModal(false)
    setDropdownOpen(false)
    setEditMode(false)
    setSuccess(false)
    setSelected('')
    setFromEdit('')
    setToEdit('')
    setSiteEdit('')
    setBlackoutId('')
    setReasonEdit('')
    resetErrors()
  }

  const otherChange = e => {
    setSelected({ value: 'other', label: `Other - ${e.target.value}` })
  }

  return (
    <>
      <DeleteBlockedModal getDate={getDate} showModal={showDeleteModal}
                          resData={{ blackoutId, fromEdit, toEdit, siteEdit }}
                          onClose={() => setShowDeleteModal(false)} />
      <ViewModal className={calendarModalClasses} id="calendar-block-modal"
                 isOpen={modal} size="lg" showCloseIcon toggle={onClosed} showOkButton={false}
                 title={editMode ? 'Blocked site' : 'Block Off Site'}>
        {!editMode && (!from || !to) && <Alert color="danger">Please select a valid date range</Alert>}
        {editMode &&
          <div className="mb-2">
            <div>Site Name:</div>
            <div className="text-break">{siteEdit}</div>
          </div>
        }
        <div className="my-2">
          {editMode ?
            (<span>Reason for Block:</span>) :
            (<span>Why are you blocking off this site?<span className="text-danger"><sup>*</sup></span></span>)
          }
        </div>
        <CalendarBlockForm disabled={!editMode && (!from || !to)} isEngineAdmin={isEngineAdmin}
                           onChange={setSelected} onDropdown={setDropdownOpen} value={editMode ? reasonEdit : ''} />
        {(selected.value === 'other' || (reasonEdit && reasonEdit.includes('Other - '))) && (
          <div className="my-2">
            <span className="fw-bold">What's the reason for this block?</span>
            <textarea className="form-control" defaultValue={reasonEdit ? reasonEdit.split('Other - ')[1] : ''} onChange={otherChange} />
          </div>
        )}
        <br />
        {success && <Alert>Date block {editMode ? 'updated' : 'added'}</Alert>}
        {error !== undefined && <Alert color="danger">Error: {error.message}</Alert>}
        {editMode ? (
          <div className="w-full mb-2">
            <span>Dates:</span>
            <div>{fromEdit} - {toEdit}</div>
          </div>) : (<div className="d-flex justify-content-end buttons-cont">
            <Button color="salmon" className="mx-1 btn-white-salmon control-item-white-salmon"
                    onClick={editMode ? toggleEdit : toggle}>Cancel</Button>
            <Button color="salmon" className="mx-1" onClick={handleBlockSite}
                    disabled={loading || success || !from || !to || !selected?.label}>
              {loading ? 'Creating...' : 'Block Off Site'}
            </Button>
          </div>
        )}
        {editMode && (
          <div className="d-flex justify-content-between buttons-cont-edit">
            <Button color="salmon" className="mx-1 btn-white-salmon control-item-white-salmon"
                    onClick={() => { toggle(); setShowDeleteModal(true) }}>Unblock Site</Button>
            <Button color="salmon" className="mx-1" onClick={handleUpdateSite}
                    disabled={updating || success || !selected?.label || selected?.label === reasonEdit}>
              {updating ? 'Updating...' : 'Update Block'}
            </Button>
          </div>
        )}
        {updateError && <Alert color="danger">Update Error: {updateError.message}</Alert>}
      </ViewModal>
    </>
  )
}

export default CalendarBlock
